/**
 * Rate Limiting Service
 * Provides comprehensive rate limiting for API endpoints, authentication, and user actions
 */

export interface RateLimitConfig {
  // Authentication rate limits
  authLoginAttempts: number; // per 15 minutes
  authPasswordReset: number; // per hour
  authEmailVerification: number; // per hour
  
  // API rate limits
  apiRequestsPerMinute: number;
  apiRequestsPerHour: number;
  apiRequestsPerDay: number;
  
  // File upload rate limits
  fileUploadsPerMinute: number;
  fileUploadsPerHour: number;
  maxFileSize: number; // in bytes
  
  // Workspace creation rate limits
  workspaceCreationPerHour: number;
  workspaceCreationPerDay: number;
  
  // Email sending rate limits
  emailSendsPerMinute: number;
  emailSendsPerHour: number;
  
  // Chat message rate limits
  chatMessagesPerMinute: number;
  chatMessagesPerHour: number;
}

export interface RateLimitResult {
  allowed: boolean;
  remaining: number;
  resetTime: number;
  retryAfter?: number;
}

class RateLimitingService {
  private config: RateLimitConfig;
  private storage: Map<string, { count: number; resetTime: number }> = new Map();
  
  constructor(config: RateLimitConfig) {
    this.config = config;
    this.cleanupExpiredEntries();
  }

  /**
   * Check if an action is allowed for a user
   */
  async checkRateLimit(
    action: string,
    userId?: string,
    identifier?: string
  ): Promise<RateLimitResult> {
    const key = this.getRateLimitKey(action, userId, identifier);
    const now = Date.now();
    
    // Get current rate limit data
    const current = this.storage.get(key);
    
    if (!current || now > current.resetTime) {
      // No rate limit data or expired, create new entry
      const resetTime = this.getResetTime(action);
      this.storage.set(key, { count: 1, resetTime });
      
      return {
        allowed: true,
        remaining: this.getLimit(action) - 1,
        resetTime
      };
    }
    
    // Check if limit exceeded
    if (current.count >= this.getLimit(action)) {
      return {
        allowed: false,
        remaining: 0,
        resetTime: current.resetTime,
        retryAfter: Math.ceil((current.resetTime - now) / 1000)
      };
    }
    
    // Increment count
    current.count++;
    this.storage.set(key, current);
    
    return {
      allowed: true,
      remaining: this.getLimit(action) - current.count,
      resetTime: current.resetTime
    };
  }

  /**
   * Record a rate-limited action
   */
  async recordAction(
    action: string,
    userId?: string,
    identifier?: string
  ): Promise<void> {
    const key = this.getRateLimitKey(action, userId, identifier);
    const now = Date.now();
    
    const current = this.storage.get(key);
    
    if (!current || now > current.resetTime) {
      const resetTime = this.getResetTime(action);
      this.storage.set(key, { count: 1, resetTime });
    } else {
      current.count++;
      this.storage.set(key, current);
    }
  }

  /**
   * Get rate limit key
   */
  private getRateLimitKey(action: string, userId?: string, identifier?: string): string {
    const parts = [action];
    if (userId) parts.push(`user:${userId}`);
    if (identifier) parts.push(`id:${identifier}`);
    return parts.join(':');
  }

  /**
   * Get reset time for action
   */
  private getResetTime(action: string): number {
    const now = Date.now();
    
    switch (action) {
      case 'auth:login':
        return now + (15 * 60 * 1000); // 15 minutes
      case 'auth:password_reset':
      case 'auth:email_verification':
        return now + (60 * 60 * 1000); // 1 hour
      case 'api:requests':
        return now + (60 * 1000); // 1 minute
      case 'file:upload':
        return now + (60 * 1000); // 1 minute
      case 'workspace:create':
        return now + (60 * 60 * 1000); // 1 hour
      case 'email:send':
        return now + (60 * 1000); // 1 minute
      case 'chat:message':
        return now + (60 * 1000); // 1 minute
      default:
        return now + (60 * 1000); // Default 1 minute
    }
  }

  /**
   * Get limit for action
   */
  private getLimit(action: string): number {
    switch (action) {
      case 'auth:login':
        return this.config.authLoginAttempts;
      case 'auth:password_reset':
        return this.config.authPasswordReset;
      case 'auth:email_verification':
        return this.config.authEmailVerification;
      case 'api:requests':
        return this.config.apiRequestsPerMinute;
      case 'file:upload':
        return this.config.fileUploadsPerMinute;
      case 'workspace:create':
        return this.config.workspaceCreationPerHour;
      case 'email:send':
        return this.config.emailSendsPerMinute;
      case 'chat:message':
        return this.config.chatMessagesPerMinute;
      default:
        return 10; // Default limit
    }
  }

  /**
   * Clean up expired rate limit entries
   */
  private cleanupExpiredEntries(): void {
    const now = Date.now();
    for (const [key, value] of this.storage.entries()) {
      if (now > value.resetTime) {
        this.storage.delete(key);
      }
    }
  }

  /**
   * Get rate limit status for a user
   */
  async getRateLimitStatus(
    action: string,
    userId?: string,
    identifier?: string
  ): Promise<RateLimitResult> {
    const key = this.getRateLimitKey(action, userId, identifier);
    const current = this.storage.get(key);
    const now = Date.now();
    
    if (!current || now > current.resetTime) {
      return {
        allowed: true,
        remaining: this.getLimit(action),
        resetTime: this.getResetTime(action)
      };
    }
    
    return {
      allowed: current.count < this.getLimit(action),
      remaining: Math.max(0, this.getLimit(action) - current.count),
      resetTime: current.resetTime
    };
  }

  /**
   * Reset rate limit for a user/action
   */
  async resetRateLimit(
    action: string,
    userId?: string,
    identifier?: string
  ): Promise<void> {
    const key = this.getRateLimitKey(action, userId, identifier);
    this.storage.delete(key);
  }

  /**
   * Get all rate limit data (for debugging)
   */
  getRateLimitData(): Record<string, { count: number; resetTime: number }> {
    const data: Record<string, { count: number; resetTime: number }> = {};
    for (const [key, value] of this.storage.entries()) {
      data[key] = value;
    }
    return data;
  }
}

// Default configuration
const defaultConfig: RateLimitConfig = {
  // Authentication rate limits
  authLoginAttempts: 5, // 5 attempts per 15 minutes
  authPasswordReset: 3, // 3 resets per hour
  authEmailVerification: 5, // 5 verifications per hour
  
  // API rate limits
  apiRequestsPerMinute: 60,
  apiRequestsPerHour: 1000,
  apiRequestsPerDay: 10000,
  
  // File upload rate limits
  fileUploadsPerMinute: 10,
  fileUploadsPerHour: 100,
  maxFileSize: 10 * 1024 * 1024, // 10MB
  
  // Workspace creation rate limits
  workspaceCreationPerHour: 5,
  workspaceCreationPerDay: 20,
  
  // Email sending rate limits
  emailSendsPerMinute: 10,
  emailSendsPerHour: 100,
  
  // Chat message rate limits
  chatMessagesPerMinute: 30,
  chatMessagesPerHour: 500
};

// Create singleton instance
export const rateLimiting = new RateLimitingService(defaultConfig); 